import React, { useCallback, useEffect, useState } from 'react';
import {
  getMindMapSharing,
  removeMindMapMember,
  updateMindMapLinkAccess,
  upsertMindMapMember,
} from '../../utils/mindmap/apiClient';

export function buildShareUrl(mindmapId, shareToken = null) {
  if (typeof window === 'undefined') return '';
  const url = new URL(window.location.href);
  url.search = '';
  url.searchParams.set('map', mindmapId);
  url.hash = shareToken
    ? new URLSearchParams({ share: shareToken }).toString()
    : '';
  return url.toString();
}

async function copyText(value) {
  if (!value) return false;
  if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
    return false;
  }
  await navigator.clipboard.writeText(value);
  return true;
}

const ROLE_LABELS = {
  viewer: 'Viewer',
  editor: 'Editor',
};

const LINK_OPTIONS = [
  ['restricted', 'Restricted'],
  ['viewer', 'Anyone with the link can view'],
  ['editor', 'Anyone with the link can edit'],
];

export default function ShareDialog({
  isOpen,
  onClose,
  mindmapId,
  canManage,
  isDarkMode,
  showNotification,
}) {
  const [members, setMembers] = useState([]);
  const [linkAccess, setLinkAccess] = useState('restricted');
  const [currentShareUrl, setCurrentShareUrl] = useState(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const notify = useCallback((message, variant = 'info') => {
    if (typeof showNotification === 'function') {
      showNotification(message, variant);
    }
  }, [showNotification]);

  useEffect(() => {
    if (!isOpen || !canManage || !mindmapId) return undefined;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    getMindMapSharing(mindmapId)
      .then((settings) => {
        if (cancelled) return;
        setMembers(settings.members || []);
        setLinkAccess(settings.linkAccess || 'restricted');
        setCurrentShareUrl(null);
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError.message);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canManage, isOpen, mindmapId]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const panelStyle = {
    width: 'min(560px, calc(100vw - 32px))',
    maxHeight: 'calc(100vh - 48px)',
    overflowY: 'auto',
    borderRadius: 16,
    padding: 22,
    background: isDarkMode ? '#242424' : '#ffffff',
    color: isDarkMode ? '#f9fafb' : '#111827',
    boxShadow: '0 24px 70px rgba(0, 0, 0, 0.38)',
    fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  };
  const fieldStyle = {
    border: `1px solid ${isDarkMode ? '#52525b' : '#d1d5db'}`,
    borderRadius: 9,
    padding: '9px 10px',
    background: isDarkMode ? '#18181b' : '#ffffff',
    color: 'inherit',
    font: 'inherit',
  };
  const buttonStyle = {
    border: 0,
    borderRadius: 999,
    padding: '9px 14px',
    background: '#22c55e',
    color: '#052e16',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
  };
  const quietButtonStyle = {
    ...buttonStyle,
    background: isDarkMode ? '#3f3f46' : '#e5e7eb',
    color: isDarkMode ? '#f9fafb' : '#111827',
  };

  const handleCopy = async (url) => {
    try {
      if (await copyText(url)) {
        notify('Map link copied to clipboard.', 'success');
      } else {
        notify(`Map link: ${url}`, 'info');
      }
    } catch {
      notify(`Map link: ${url}`, 'info');
    }
  };

  const handleMemberSubmit = async (event) => {
    event.preventDefault();
    if (!email.trim() || isSaving) return;
    setIsSaving(true);
    setError(null);
    try {
      const member = await upsertMindMapMember(
        mindmapId,
        email.trim().toLowerCase(),
        role
      );
      setMembers((previous) => [
        ...previous.filter((item) => item.userId !== member.userId),
        member,
      ].sort((left, right) => left.email.localeCompare(right.email)));
      setEmail('');
      notify(`${member.email} can now ${role === 'editor' ? 'edit' : 'view'} this map.`, 'success');
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleMemberRole = async (member, nextRole) => {
    setIsSaving(true);
    setError(null);
    try {
      const updated = await upsertMindMapMember(mindmapId, member.email, nextRole);
      setMembers((previous) => previous.map((item) => (
        item.userId === updated.userId ? updated : item
      )));
      notify(`${updated.email} is now a ${ROLE_LABELS[nextRole].toLowerCase()}.`, 'success');
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveMember = async (member) => {
    setIsSaving(true);
    setError(null);
    try {
      await removeMindMapMember(mindmapId, member.userId);
      setMembers((previous) => previous.filter((item) => item.userId !== member.userId));
      notify(`${member.email} no longer has named access.`, 'success');
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setIsSaving(false);
    }
  };

  const changeLinkAccess = async (nextAccess, rotate = false) => {
    setIsSaving(true);
    setError(null);
    try {
      const result = await updateMindMapLinkAccess(
        mindmapId,
        nextAccess,
        rotate
      );
      setLinkAccess(result.linkAccess);
      if (result.shareToken) {
        const url = buildShareUrl(mindmapId, result.shareToken);
        setCurrentShareUrl(url);
        await handleCopy(url);
      } else if (result.linkAccess === 'restricted') {
        setCurrentShareUrl(null);
        notify('Link access disabled.', 'success');
      } else {
        notify(`Existing link access changed to ${ROLE_LABELS[result.linkAccess].toLowerCase()}.`, 'success');
      }
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setIsSaving(false);
    }
  };

  const ordinaryMapUrl = buildShareUrl(mindmapId);

  return (
    <div
      role="presentation"
      onMouseDown={(event) => {
        event.stopPropagation();
        if (event.target === event.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        background: 'rgba(0, 0, 0, 0.48)',
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-dialog-title"
        style={panelStyle}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <h2 id="share-dialog-title" style={{ margin: 0, fontSize: 22 }}>Share map</h2>
            <p style={{ margin: '5px 0 18px', opacity: 0.75, fontSize: 13 }}>
              Link recipients sign in so edits and presence remain attributable.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close sharing dialog" style={quietButtonStyle}>×</button>
        </div>

        {!canManage ? (
          <div>
            <p>Only the map owner can change access. This link works for people who already have permission.</p>
            <button type="button" onClick={() => handleCopy(ordinaryMapUrl)} style={buttonStyle}>
              Copy map link
            </button>
          </div>
        ) : isLoading ? (
          <p role="status">Loading sharing settings…</p>
        ) : (
          <>
            <form onSubmit={handleMemberSubmit}>
              <h3 style={{ margin: '4px 0 10px', fontSize: 16 }}>People with access</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto auto', gap: 8 }}>
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="name@example.com"
                  aria-label="Collaborator email"
                  style={{ ...fieldStyle, minWidth: 0 }}
                  required
                />
                <select value={role} onChange={(event) => setRole(event.target.value)} aria-label="Collaborator role" style={fieldStyle}>
                  <option value="viewer">Viewer</option>
                  <option value="editor">Editor</option>
                </select>
                <button type="submit" disabled={isSaving} style={buttonStyle}>Add</button>
              </div>
              <p style={{ margin: '7px 0 14px', fontSize: 12, opacity: 0.72 }}>
                They must have signed into Mindmappi once before you add them.
              </p>
            </form>

            {members.length > 0 && (
              <div style={{ display: 'grid', gap: 8, marginBottom: 22 }}>
                {members.map((member) => (
                  <div key={member.userId} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto auto', gap: 8, alignItems: 'center' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {member.displayName || member.email}
                      </div>
                      {member.displayName && <div style={{ fontSize: 12, opacity: 0.7 }}>{member.email}</div>}
                    </div>
                    <select
                      value={member.role}
                      onChange={(event) => handleMemberRole(member, event.target.value)}
                      disabled={isSaving}
                      aria-label={`Role for ${member.email}`}
                      style={fieldStyle}
                    >
                      <option value="viewer">Viewer</option>
                      <option value="editor">Editor</option>
                    </select>
                    <button type="button" onClick={() => handleRemoveMember(member)} disabled={isSaving} style={quietButtonStyle}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div style={{ borderTop: `1px solid ${isDarkMode ? '#3f3f46' : '#e5e7eb'}`, paddingTop: 18 }}>
              <h3 style={{ margin: '0 0 10px', fontSize: 16 }}>General access</h3>
              <select
                value={linkAccess}
                onChange={(event) => changeLinkAccess(event.target.value)}
                disabled={isSaving}
                aria-label="General link access"
                style={{ ...fieldStyle, width: '100%' }}
              >
                {LINK_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                {linkAccess === 'restricted' ? (
                  <button type="button" onClick={() => handleCopy(ordinaryMapUrl)} style={quietButtonStyle}>
                    Copy link for named people
                  </button>
                ) : currentShareUrl ? (
                  <button type="button" onClick={() => handleCopy(currentShareUrl)} style={buttonStyle}>
                    Copy anyone link
                  </button>
                ) : (
                  <button type="button" onClick={() => changeLinkAccess(linkAccess, true)} disabled={isSaving} style={buttonStyle}>
                    Replace and copy link
                  </button>
                )}
              </div>
              {linkAccess !== 'restricted' && !currentShareUrl && (
                <p style={{ margin: '8px 0 0', fontSize: 12, opacity: 0.72 }}>
                  The existing secret cannot be retrieved. Replacing it revokes the previous link immediately.
                </p>
              )}
            </div>
          </>
        )}

        {error && <p role="alert" style={{ margin: '16px 0 0', color: '#ef4444', fontWeight: 700 }}>{error}</p>}
      </section>
    </div>
  );
}
